import Vue from 'vue';
import Router, { RawLocation, Route, NavigationGuard } from 'vue-router';
import Home from './views/Home.vue';
import LoginPage from './views/LoginPage.vue';
import CitationRoot from './Citation/Root.vue';
import PermitRoot from './Permit/PermitRoot.vue';
import { PermitMode } from './Permit/Enum';
import ReportingRoot from './Reporting/ReportingRoot.vue';
import { typedStore } from './store';
import Roles from '@/Shared/Roles';
import { stringCompare } from './Shared/Utilities';
import { ReturnAfterLogin } from './Stores/UserStore';
import { redirectRetiredLocation } from './Shared/Redirection';

Vue.use(Router);

export const externalRedirectionGuard: NavigationGuard = async (to, from, next) => {
    if (to.params.location) {
        await redirectRetiredLocation(to.params.location);
    }

    next();
};

export const authorizeInternalNavigation: NavigationGuard = async (to, from, next) => {
    const user = await typedStore.modules.userModule.actions.getUser();
    const authenticated = await typedStore.modules.userModule.actions.getAuthenticationStatus();

    if (!authenticated) {
        await typedStore.modules.userModule.actions.removeChecker();
        if (to
            && to.name !== 'vatsLogin'
            && to.name !== 'login'
            && to?.name !== 'internalExternal') {
            const previousRoute: ReturnAfterLogin = { ...to, name: to.name ?? undefined };
            typedStore.modules.userModule.mutations.setReturnAfterLogin(previousRoute);
        }
        next({ name: 'vatsLogin' });
    }
    else if (to.params.location) {
        const locations = await typedStore.modules.userModule.actions.getAllowedLocations();
        const roles = await typedStore.modules.userModule.actions.getCurrentRoles();

        const isInRole = typedStore.modules.userModule.getters.isInRole;
        const roleRedirects = {
            [Roles.MobileEnforcer]: '/main/IssueTicketsCam.aspx',
            [Roles.ScofflawSearch]: '/scofflawsearch/default.aspx',
            [Roles.HearingOfficer]: '/PermitCheck/default.aspx',
            [Roles.LpiUser]: '/LPI/default.aspx',
        };

        const redirectRole = Object.keys(roleRedirects).find(key => isInRole(key));

        if (redirectRole) {
            window.location.href = roleRedirects[redirectRole];
            next(false);
        }
        else {
            const newLocation = locations.find(item => stringCompare(to.params.location, item.urlSegment));
            if (newLocation) {
                typedStore.actions.changeCurrentLocation(newLocation).then(next);
                await typedStore.modules.userModule.actions.startInactiveUserCheck();
            }
            else {
                next('/access-denied');
            }
        }
    }
    else {
        next();
    }
};

const citationPaymentProps = (route: Route) => ({
    location: route.params.location,
    searchCitationIdProp: route.query.citation || route.query.recid,
    searchPlate: route.query.plate,
    searchState: route.query.state,
});

const permitPaymentProps = (route: Route) => ({
    location: route.params.location,
    permitID: route.params.permitID,
});

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: '/',
            name: 'home',
            component: Home,
        },
        {
            path: '/vats/login',
            name: 'vatsLogin',
            component: LoginPage,
        },
        {
            path: '/vats/reset-password',
            name: 'passwordResetRoot',
            props: true,
            component: () => import('./Shared/ViewPort.vue'),
            redirect: '/vats/reset-password/request',
            children: [
                {
                    path: 'request',
                    name: 'passwordResetRequest',
                    component: () => import('./User/PasswordResetRequest.vue'),
                },
                {
                    path: 'complete',
                    name: 'passwordResetCompletion',
                    props: route => ({ requestId: route.query.requestId }),
                    component: () => import('./User/PasswordResetCompletion.vue')
                }
            ]
        },
        {
            path: '/vats',
            name: 'internalExternal',
            props: true,
            component: () => import('./LocationRoot/LocationRoot.vue'),
            children: [
                {
                    path: 'payment',
                    name: 'paymentRoot',
                    props: true,
                    component: () => import('./Shared/ViewPort.vue'),
                    // Shouldn't come up, but Citations are by far the most common thing paid
                    redirect: '/vats/payment/citation',
                    children: [
                        {
                            path: 'citation/:location?',
                            name: 'citationPaymentNoLocation',
                            component: () => import('./Payment/PaymentLocation.vue'),
                            props: (route: Route) => ({
                                destinationRoute: 'citationPayment',
                                location: route.params.location,
                                unknownLocation: route.params.unknownLocation || false,
                            }),
                            children: [
                                {
                                    path: '/vats/:location/payment/citation',
                                    props: citationPaymentProps,
                                    component: () => import('./Shared/ViewPort.vue'),
                                    children: [
                                        {
                                            name: 'citationPayment',
                                            path: '',
                                            props: citationPaymentProps,
                                            beforeEnter: externalRedirectionGuard,
                                            component: () => import('./Citation/Payment/CitationPayment.vue'),
                                        },
                                        {
                                            name: 'citationPaymentCompletionError',
                                            path: 'completion/error',
                                            props: true,
                                            component: () => import('./Citation/Payment/Error.vue'),
                                        },
                                        {
                                            path: 'completion/:responseId',
                                            name: 'citationPaymentCompletion',
                                            component: () => import('./Citation/Payment/PaymentCompletion.vue'),
                                            props: true,
                                        },
                                        {
                                            path: 'BootRelease/Completion/:holdId',
                                            props: true,
                                            name: 'citationBootReleaseCompletion',
                                            component: () => import('./Citation/Payment/ReleaseCompletion.vue'),
                                        },
                                        {
                                            path: 'PayPal/Completion/:transactionId',
                                            props: true,
                                            name: 'citationPayPalPaymentCompletion',
                                            component: () => import('./Citation/Payment/CitationPaypalCompletion.vue'),
                                        },
                                        {
                                            path: 'Five9/Completion/:transactionId',
                                            props: true,
                                            name: 'five9PaymentCompletion',
                                            component: () => import('./Payment/Five9Completion.vue'),
                                        },
                                        {
                                            path: 'BootRelease/PayPal/Completion/:transactionId',
                                            props: true,
                                            name: 'citationPayPalBootReleaseCompletion',
                                            component: () => import('./Citation/Payment/PaypalReleaseCompletion.vue'),
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: 'permit/:location?',
                            name: 'permitPurchaseNoLocation',
                            props: (route: Route) =>
                            ({
                                location: route.params.location,
                                shouldRedirect: route.name === 'permitPurchaseNoLocation',
                                destinationRoute: 'permitPurchase'
                            }),
                            component: PermitRoot,
                            children: [
                                {
                                    path: '/vats/:location/purchase/permit',
                                    props: permitPaymentProps,
                                    beforeEnter: externalRedirectionGuard,
                                    component: () => import('./Shared/ViewPort.vue'),
                                    children: [
                                        {
                                            path: '',
                                            name: 'permitPurchase',
                                            redirect: { name: 'permitApply' },
                                        },
                                        {
                                            path: 'apply',
                                            name: 'permitApply',
                                            component: () => import('./Permit/PermitPurchase.vue'),
                                            props: (route: Route) =>
                                            ({
                                                location: route.params.location,
                                                permitMode: PermitMode.PermitApply
                                            }),
                                        },
                                        {
                                            path: 'find/:permitID?',
                                            name: 'permitFind',
                                            component: () => import('./Permit/PermitPurchase.vue'),
                                            props: (route: Route) =>
                                            ({
                                                location: route.params.location,
                                                permitMode: PermitMode.PermitFind,
                                                permitID: route.params.permitID
                                            }),
                                        },
                                        {
                                            path: '/vats/:location/permit/payment/:permitID',
                                            name: 'permitPayment',
                                            props: true,
                                            component: () => import('./Permit/Payment/PermitPayment.vue')
                                        },
                                        {
                                            path: '/vats/:location/payment/permit/completion/Error',
                                            name: 'permitPaymentResponseError',
                                            component: () => import('./Payment/PaymentError.vue'),
                                            props: true,
                                        },
                                        {
                                            path: '/vats/:location/payment/permit/paypal/completion/:transactionId',
                                            name: 'permitPaymentPaypalResponse',
                                            component: () => import('./Permit/Payment/PermitPaypalCompletion.vue'),
                                            props: true,
                                        },
                                    ]
                                }
                            ]
                        }
                    ]
                },
            ],
        },
        {
            path: '/vats/:location/payment/five9/completion/:responseId',
            name: 'five9CitationPaymentsResponse',
            component: () => import('./Citation/Payment/PaymentCompletion.vue'),
            props: true,
        },
        {
            path: '/vats/:location/payment/five9/completion/:responseId',
            name: 'five9PermitPaymentsResponse',
            component: () => import('./Payment/PaymentCompletion.vue'),
            props: true,
        },
        {
            path: '/vats/:location/payment/completion/Error',
            name: 'paymentResponseError',
            component: () => import('./Payment/PaymentError.vue'),
            props: true,
        },
        {
            path: '/vats/:location',
            name: 'locationRoot',
            redirect: '/vats/:location/home',
            component: () => import('./LocationRoot/LocationRoot.vue'),
            props: true,
            beforeEnter: authorizeInternalNavigation,
            children:
                [
                    {
                        path: 'home',
                        name: 'locationHome',
                        component: () => import('./LocationHome/LocationHome.vue'),
                        props: true,
                    },
                    {
                        path: 'reporting',
                        name: 'reportingroot',
                        component: ReportingRoot,
                        props: true,
                        children: [
                            {
                                path: 'reportinggenerator',
                                name: 'reportingGenerator',
                                component: () => import('./Reporting/ReportingGenerator/ReportingGenerator.vue'),
                                props: true
                            }
                        ]
                    },
                    {
                        path: 'citation',
                        name: 'citationRoot',
                        component: CitationRoot,
                        props: true,
                        children: [
                            {
                                path: 'search',
                                name: 'citationSearch',
                                component: () => import('./Citation/CitationSearch/CitationSearch.vue'),
                                props: true
                            },
                            {
                                path: 'search/results',
                                name: 'citationSearchResults',
                                component: () => import('./Citation/CitationSearch/SearchResults.vue'),
                                props: true
                            },

                        ]
                    },
                    {
                        path: 'ticket',
                        name: 'issueTicket',
                        component: () => import('./Citation/IssueTicket/IssueTicket.vue'),
                        props: true
                    },
                    {
                        path: 'admin',
                        name: 'adminRoot',
                        component: () => import('./Admin/AdminRoot.vue'),
                        props: true,
                        beforeEnter(to, from, next) {
                            const isInRole = typedStore.modules.userModule.getters.isInRole;
                            typedStore.modules.userModule.actions.getCurrentRoles().then(() => {
                                if (isInRole(Roles.SystemAdmin) || isInRole(Roles.CompanyAdmin)) {
                                    next();
                                }
                                else {
                                    next('/access-denied');
                                }
                            });
                        },
                        children: [
                            {
                                path: 'table-management',
                                name: 'tableManagementAdmin',
                                props: true,
                                component: () => import('./Admin/TableManagement/TableManagement.vue')
                            },
                            {
                                path: 'vats-tracking',
                                name: 'vatsTracking',
                                props: true,
                                component: () => import('./Admin/VATSTracking/VATSTracking.vue')
                            },
                            {
                                path: 'customer-alias',
                                name: 'customerAlias',
                                props: true,
                                component: () => import('./Admin/CustomerAlias/CustomerAlias.vue')
                            },
                            {
                                path: 'letter-match',
                                name: 'letterMatchAdmin',
                                props: true,
                                component: () => import('./Admin/LetterMatch/LetterMatchAdmin.vue'),
                            },
                            {
                                path: 'auto-reports-maintenance',
                                name: 'autoReportsMaintenance',
                                props: true,
                                component: () => import('./Admin/AutoReportsMaintenance/AutoReportsMaintenance.vue')
                            },
                            {
                                path: 'release-notes',
                                name: 'releaseNotesAdmin',
                                props: true,
                                component: () => import('./Admin/ReleaseNotes/ReleaseNotesAdmin.vue')
                            },
                            {
                                path: 'handheld-users',
                                name: 'handheldUserAdmin',
                                props: true,
                                component: () => import('./Admin/HandheldUsers/HandheldUserAdmin.vue')
                            },
                            {
                                path: 'handheld-message',
                                name: 'handheldMessage',
                                props: true,
                                component: () => import('./Admin/HandheldMessage/HandheldMessage.vue')
                            },
                            {
                                path: 'lot-area',
                                name: 'lotAreaAdmin',
                                props: true,
                                component: () => import('./Admin/LotArea/LotAreaAdmin.vue')
                            },
                            {
                                path: 'lpr-analysis',
                                name: 'lprAnalysis',
                                props: true,
                                component: () => import('./Admin/LPRAnalysis/LPRAnalysis.vue')
                            },
                            {
                                path: 'lot-maintenance',
                                name: 'lotMaintenance',
                                props: true,
                                component: () => import('./Admin/LotMaintenance/LotMaintenance.vue'),
                                children: [
                                    {
                                        path: 'lots',
                                        name: 'lots',
                                        props: true,
                                        component: () => import('./Admin/LotMaintenance/Lots/Lots.vue')
                                    },
                                    {
                                        path: 'violation-codes',
                                        name: 'violationCodes',
                                        props: true,
                                        component: () => import('./Admin/LotMaintenance/ViolationCodes/ViolationCodes.vue')
                                    },
                                    {
                                        path: 'lot-violation-codes',
                                        name: 'lotViolationCodes',
                                        props: true,
                                        component: () => import('./Admin/LotMaintenance/LotViolationCodes/LotViolationCodes.vue')
                                    },
                                ]
                            },
                            {
                                path: 'behavior-profiles',
                                name: 'behaviorProfiles',
                                props: true,
                                component: () => import('./Admin/LotMaintenance/BehaviorProfile.vue'),
                            },
                            {
                                path: 'vendor-lot-ids',
                                name: 'vendorLotIds',
                                props: true,
                                component: () => import('./Admin/VendorLotIds/VendorLotIds.vue'),
                            },
                            {
                                path: 'meter-mapping',
                                name: 'meterMapping',
                                props: true,
                                component: () => import('./Admin/MeterMapping/MeterMapping.vue'),
                            },
                            {
                                path: 'chalking-location-rules',
                                name: 'chalkingLocationRules',
                                props: true,
                                component: () => import('./Admin/ChalkingLocationRules/ChalkingLocationRules.vue'),
                            },
                            {
                                path: 'my-reports-management',
                                name: 'myReportsManagement',
                                props: true,
                                component: () => import('./Admin/MyReportsManagement/MyReportsManagement.vue'),
                            },
                            {
                                path: 'hearing-dates',
                                name: 'hearingDatesAdmin',
                                props: true,
                                component: () => import('./Admin/HearingBlackoutDates/HearingBlackoutDates.vue')
                            },
                            {
                                path: 'user-management',
                                name: 'userManagementAdmin',
                                props: true,
                                component: () => import('./Admin/UserManagement/UserManagementAdmin.vue')
                            },
                            {
                                path: 'notification-management',
                                name: 'notificationManagementAdmin',
                                props: true,
                                component: () =>
                                    import('./Admin/NotificationManagement/NotificationManagementAdmin.vue')
                            },
                            {
                                path: 'diagnostic-counts',
                                name: 'diagnosticCountsAdmin',
                                props: true,
                                component: () => import('./Admin/DiagnosticCounts/DiagnosticCountsAdmin.vue')
                            },
                            {
                                path: 'boot-inventory',
                                name: 'bootManagementAdmin',
                                props: true,
                                component: () => import('./Admin/BootManagement/BootManagementAdmin.vue')
                            },
                            {
                                path: 'letter-management',
                                name: 'letterManagement',
                                props: true,
                                component: () => import('./Admin/LetterManagement/LetterManagement.vue')
                            },
                            {
                                path: 'report-management',
                                name: 'reportManagement',
                                props: true,
                                component: () => import('./Admin/ReportManagement/ReportManagement.vue')
                            },
                            {
                                path: 'bulk-hold-request',
                                name: 'bulkholdrequest',
                                props: true,
                                component: () => import('./Admin/BulkHoldRequest/BulkHoldRequest.vue')
                            },
                            {
                                path: 'customer-file-history',
                                name: 'customerFileHistory',
                                props: true,
                                component: () => import('./Admin/CustomerFileHistory/CustomerFileHistory.vue'),
                            },
                            {
                                path: 'customer-file-details',
                                name: 'customerFileDetails',
                                props: true,
                                component: () => import('./Admin/CustomerFileHistory/CustomerFileDetails/CustomerFileDetails.vue')
                            },
                            {
                                path: 'reversal-reason-management',
                                name: 'reversalReasonManagement',
                                props: true,
                                component: () => import('./Admin/ReversalReasonManagement/ReversalReasonManagement.vue')
                            },
                            {
                                path: 'permit-management',
                                name: 'permitManagement',
                                props: true,
                                component: () => import('./Admin/PermitManagement/PermitManagement.vue'),
                                children: [
                                    {
                                        path: 'permit-types',
                                        name: 'permitTypes',
                                        props: true,
                                        component: () => import('./Admin/PermitManagement/PermitTypes/PermitTypes.vue')
                                    },
                                    {
                                        path: 'permit-categories',
                                        name: 'permitCategories',
                                        props: true,
                                        component: () => import('./Admin/PermitManagement/PermitCategories/PermitCategories.vue')
                                    },
                                    {
                                        path: 'permit-settings',
                                        name: 'permitSettings',
                                        props: true,
                                        component: () => import('./Admin/PermitManagement/PermitSettings/PermitSettings.vue')
                                    },
                                    {
                                        path: 'permit-application-documents',
                                        name: 'documentsList',
                                        props: true,
                                        component: () => import('./Admin/PermitManagement/ApplicationDocuments/DocumentsList.vue')
                                    },
                                ],
                            },
                            {
                                path: 'program-variables',
                                name: 'programVariables',
                                props: true,
                                component: () => import('./Admin/ProgramVariables/ProgramVariables.vue'),
                                children: [
                                    {
                                        path: 'location-config',
                                        name: 'locationConfig',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/LocationConfig/LocationConfig.vue')
                                    },
                                    {
                                        path: 'escalation-management',
                                        name: 'escalationManagement',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/EscalationManagement/EscalationManagement.vue'),
                                        children: [
                                            {
                                                path: 'citation-escalations',
                                                name: 'citationEscalations',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/EscalationManagement/CitationEscalations/CitationEscalations.vue')
                                            },
                                            {
                                                path: 'escalation-group',
                                                name: 'escalationGroup',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/EscalationManagement/EscalationGroup/EscalationGroup.vue')
                                            },
                                        ],
                                    },
                                    {
                                        path: 'payments',
                                        name: 'paymentsConfig',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Payments/PaymentConfig.vue'),
                                        children: [
                                            {
                                                path: 'citation-payments',
                                                name: 'citationPaymentsConfig',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/Payments/CitationPayments/CitationPayments.vue')
                                            },
                                            {
                                                path: 'permit-payments',
                                                name: 'permitPaymentsConfig',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/Payments/PermitPayments/PermitPaymentsConfig.vue')
                                            },
                                            {
                                                path: 'processor-config',
                                                name: 'paymentProcessorConfig',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/Payments/PaymentProcessor/ProcessorConfig.vue')
                                            }
                                        ]
                                    },
                                    {
                                        path: 'handhelds',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/HandheldVariables/HandheldParent.vue'),
                                        children: [
                                            {
                                                path: '',
                                                name: 'handheldVariables',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/HandheldVariables/HandheldVariables.vue'),
                                            },
                                            {
                                                path: 'ocr-state-mapping',
                                                name: 'ocrStateMapping',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/HandheldVariables/StateMapping.vue'),
                                            },
                                            {
                                                path: 'ocr-make-mapping',
                                                name: 'ocrMakeMapping',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/HandheldVariables/MakeMapping.vue'),
                                            },
                                            {
                                                path: 'ocr-color-mapping',
                                                name: 'ocrColorMapping',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/HandheldVariables/ColorMapping.vue'),
                                            }
                                        ]
                                    },
                                    {
                                        path: 'hearings',
                                        name: 'hearingsVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Hearings/HearingsVariables.vue')
                                    },
                                    {
                                        path: 'appeals',
                                        name: 'appealsVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Appeals/AppealsVariables.vue')
                                    },
                                    {
                                        path: 'bagging',
                                        name: 'baggingVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Bagging/BaggingVariables.vue')
                                    },
                                    {
                                        path: 'collections',
                                        name: 'collectionsVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Collections/CollectionsVariables.vue')
                                    },
                                    {
                                        path: 'letters',
                                        name: 'lettersSystemVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Letters/LettersSystemVariables.vue')
                                    },
                                    {
                                        path: 'lookups',
                                        name: 'lookupsSystemVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Lookups/LookupsSystemVariables.vue'),
                                        children: [
                                            {
                                                path: 'in-state-lookups-variables',
                                                name: 'inStateLookupsVariables',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/Lookups/InStateLookupsVariables/InStateLookupsVariables.vue')
                                            },
                                            {
                                                path: 'out-of-state-lookups-variables',
                                                name: 'outOfStateLookupsVariables',
                                                props: true,
                                                component: () => import('./Admin/ProgramVariables/Lookups/OutOfStateLookupsVariables/OutOfStateLookupsVariables.vue')
                                            },
                                        ]
                                    },
                                    {
                                        path: 'reports',
                                        name: 'reportsSystemVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Reports/ReportsSystemVariables.vue')
                                    },
                                    {
                                        path: 'scofflaw',
                                        name: 'scofflawVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Scofflaw/ScofflawVariables.vue')
                                    },
                                    {
                                        path: 'holds',
                                        name: 'holdsVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Holds/HoldsVariables.vue')
                                    },
                                    {
                                        path: 'booting',
                                        name: 'bootingVariables',
                                        props: true,
                                        component: () => import('./Admin/ProgramVariables/Booting/BootingVariables.vue')
                                    },
                                ],
                            },
                            {
                                path: 'text-overrides',
                                name: 'localizationOverrides',
                                props: true,
                                component: () => import('./Admin/LocalizationOverrides/LocalizationOverrides.vue'),
                            },
                            {
                                path: 'revenue',
                                name: 'revenue',
                                props: true,
                                component: () => import('./Admin/Revenue/RevenueAndTaxes.vue'),
                            },
                            {
                                path: 'ticket-template',
                                name: 'ticketTemplates',
                                props: true,
                                component: () => import('./Admin/TicketTemplate/TicketTemplate.vue'),
                            },
                            {
                                path: 'template-builder/:templateId',
                                name: 'templateBuilder',
                                props: ({ params }) => Object.assign({
                                    ...params
                                },
                                    {
                                        templateId: Number.parseInt(params.templateId, 10) || 0
                                    }),
                                component: () => import('./Admin/TicketTemplate/TemplateBuilder.vue'),
                            }
                        ]
                    },
                    {
                        path: 'appeals',
                        name: 'AppealRoot',
                        component: () => import('./Appeals/AppealRoot.vue'),
                        props: true,
                        beforeEnter(to, from, next) {
                            const isInRole = typedStore.modules.userModule.getters.isInRole;
                            typedStore.modules.userModule.actions.getCurrentRoles().then(() => {
                                if (isInRole(Roles.SystemAdmin) || isInRole(Roles.CompanyAdmin)) {
                                    next();
                                }
                                else {
                                    next('/access-denied');
                                }
                            });
                        },
                        children: [
                            {
                                path: 'results',
                                name: 'results',
                                component: () => import('./Appeals/Citation/SearchResults.vue'),
                                props: true
                            },
                            {
                                path: 'show-photos',
                                name: 'showPhotos',
                                props: true,
                                component: () => import('./Appeals/ShowPhotos.vue')
                            },
                            {
                                path: 'information',
                                name: 'information',
                                component: () => import('./Appeals/AppellantInformation.vue'),
                                props: true
                            },
                            {
                                path: 'appeal-info',
                                name: 'appealInfo',
                                component: () => import('./Appeals/AppealInfo.vue'),
                                props: true
                            },
                            {
                                path: 'appeal-letters',
                                name: 'appealLetters',
                                component: () => import('./Appeals/AppealLetters.vue'),
                                props: true
                            },
                            {
                                path: 'hearing-info',
                                name: 'hearingInfo',
                                component: () => import('./Appeals/HearingInfo.vue'),
                                props: true
                            },
                            {
                                path: 'hearing-letters',
                                name: 'hearingLetters',
                                component: () => import('./Appeals/HearingLetters.vue'),
                                props: true
                            },
                            {
                                path: 'previous-appeals',
                                name: 'previousAppeals',
                                component: () => import('./Appeals/PreviousAppeals.vue'),
                                props: true
                            }
                        ]
                    },
                    {
                        path: '*',
                        props: true,
                        component: () => import('./views/NotFound.vue')
                    }
                ],
        },
        {
            path: '/access-denied',
            name: 'accessDenied',
            component: () => import('./views/AccessDenied.vue'),
        },
        {
            path: '/quick-validation',
            name: 'quickValidation',
            props: true,
            component: () => import('./Permit/QuickValidation.vue'),
            meta: {
                hideNavbar: true,
            }
        },
        {
            path: '*',
            component: () => import('./views/NotFound.vue')
        }
    ],
});

router.beforeEach((to, from, next) => {
    typedStore.mutations.setPendingNavigation(to);
    next();
});

export default router;
